import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap';
import { LoaderService } from 'src/modules/shared/services/loader.service';
import { ApplicantManagementService } from 'src/modules/profile-management/services/applicant-management-service.service';
import { PopupNotificationComponent } from 'src/modules/shared/request/popup-notification/popup-notification.component';
import { AppConfigService } from 'src/modules/shared/services/app-config.service';
import { EKeyAuthService } from 'src/modules/shared/services/Ekey-auth.service';
import { Subscription } from 'rxjs';
import { Mode } from 'src/modules/shared/form-guide/utility/enums/enums';

@Component({
  selector: 'app-ekey-integration-login',
  templateUrl: './ekey-integration-login.component.html',
  styleUrls: ['./ekey-integration-login.component.scss']
})
export class EKeyIntegrationLoginComponent implements OnInit, OnDestroy {
  error: string | null = null;
  cprValue: string | null = null;
  userInfo: any = null;
  isLoading: boolean = false;
  private routeSubscription: Subscription | null = null;
  private isErrorHandled: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: EKeyAuthService,
    private appConfig: AppConfigService,
    private translate: TranslateService,
    private modalService: BsModalService,
    private applicantService: ApplicantManagementService
  ) {}

  ngOnInit() {
    this.hideLoader();
    const isProduction = JSON.parse(this.appConfig.getAppConfig().IsProduction);
    if (isProduction) {
      this.handleCallback();
    }
  }

  ngOnDestroy(): void {
    this.hideLoader();
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  async handleCallback(): Promise<void> {
    this.error = null;
    this.isErrorHandled = false;
    
    this.showLoader();
    
    try {
      this.routeSubscription = this.route.queryParams.subscribe(async params => {
        await this.processCallbackParams(params);
      });
    } catch (error) {
      this.handleGlobalError('HANDLE_CALLBACK_ERROR', 'Failed to process callback', error);
    }
  }

  private async processCallbackParams(params: any): Promise<void> {
    try {
      const code = params['code'];
      const error = params['error'];
      const errorDescription = params['error_description'];

      if (error) {
        this.handleOAuthError(error, errorDescription);
        return;
      }

      if (!code) {
        this.handleGlobalError('NO_AUTHORIZATION_CODE', 'No authorization code received');
        return;
      }

      await this.processAuthenticationCode(code);

    } catch (error) {
      this.handleGlobalError('PROCESS_PARAMS_ERROR', 'Failed to process callback parameters', error);
    }
  }

  private async processAuthenticationCode(code: string): Promise<void> {
    try {
      this.showLoader();
      
      sessionStorage.setItem('ekey_code', code);

      const response = await this.authService.handleCallback(code).catch(serviceError => {
        throw new Error(this.extractErrorMessageFromService(serviceError));
      });

      this.userInfo = this.extractUserInfo(response);
/*
      if (!this.userInfo) {
        this.handleGlobalError('NO_USER_INFO', 'No user information received from authentication service');
        return;
      }
*/
      this.cprValue = this.extractCPR(this.userInfo);
/*
      if (!this.cprValue) {
        this.handleCPRExtractionError(this.userInfo);
        return;
      }
*/
      await this.redirectToLoginWithCPR();

    } catch (error) {
      this.handleGlobalError('AUTHENTICATION_ERROR', 'Authentication failed', error);
    }
  }

  private async redirectToLoginWithCPR(): Promise<void> {
    try {
      const navigationExtras = {
        queryParams: {
          'ticket': '11224466',
          'classSessionId': 'g1dsl2sal183m328jkz',
          'cpr': this.cprValue,
          'authSource': 'ekey2',
          'timestamp': new Date().getTime()
        }
      };

      this.hideLoader();
      await this.router.navigate(['/login'], navigationExtras);

    } catch (navigationError) {
      this.handleGlobalError('NAVIGATION_ERROR', 'Failed to redirect to login page', navigationError);
    }
  }

  // CENTRALIZED ERROR HANDLING - Shows popup then navigates to error page
  private async handleGlobalError(errorCode: string, message: string, technicalError?: any): Promise<void> {
    if (this.isErrorHandled) {
      return;
    }

    this.isErrorHandled = true;
    this.hideLoader();
    
    this.error = errorCode;
    this.isLoading = false;

    console.error(`Global Error [${errorCode}]:`, message, technicalError);

    // Get user-friendly message
    const userFriendlyMessage = this.getUserFriendlyErrorMessage(errorCode, message);
    
    // Show popup first, then navigate to error page
    await this.showErrorPopupAndNavigate('Authentication Error', userFriendlyMessage, errorCode);
  }

  private async showErrorPopupAndNavigate(title: string, message: string, errorCode: string): Promise<void> {
    return new Promise((resolve) => {
      const initialState = {
        alertMode: Mode.Error,
        alertHeader: title,
        alertMassage: message,
        navigateOnExit: true,
        exitNavigationURL: 'error',
        enableNavigation: true,
        backLableText: 'Home',
        navigationURL: 'home'
      };

      this.modalService.show(PopupNotificationComponent, {
        initialState,
        ignoreBackdropClick: true,
        keyboard: false,
      });

      // Navigate to error page after a short delay to ensure popup is shown
      setTimeout(async () => {
        await this.navigateToErrorPage(errorCode, message);
        resolve();
      }, 100);
    });
  }

  private async navigateToErrorPage(errorCode: string, message: string): Promise<void> {
    try {
      await this.router.navigate(['/error'], {
        queryParams: {
          errorCode: errorCode,
          errorMessage: message,
          source: 'ekey-auth',
          timestamp: new Date().getTime()
        }
      });
    } catch (navigationError) {
      console.error('Failed to navigate to error page:', navigationError);
      // Fallback - try home page
      await this.router.navigate(['/home']);
    }
  }

  private handleOAuthError(error: string, errorDescription?: string): void {
    const errorMessages: { [key: string]: string } = {
      'access_denied': 'You cancelled the authentication process. Please try again if you want to continue.',
      'unauthorized_client': 'This application is not authorized to perform authentication. Please contact support.',
      'server_error': 'The authentication server encountered an error. Please try again later.',
      'temporarily_unavailable': 'The authentication service is temporarily unavailable. Please try again later.',
      'invalid_request': 'The authentication request was invalid. Please try again.'
    };

    let errorMessage = errorMessages[error] || `Authentication error: ${error}`;
    
    if (errorDescription) {
      errorMessage += ` Details: ${errorDescription}`;
    }

    this.handleGlobalError('OAUTH_' + error.toUpperCase(), errorMessage);
  }

  private handleCPRExtractionError(userInfo: any): void {
    const errorMessage = 'CPR (Civil Personal Registration) information was not found in your user profile. ' +
      'This information is required for authentication. ' +
      'Please ensure your eKey account has valid CPR information and try again.';

    this.handleGlobalError('CPR_NOT_FOUND', errorMessage);
  }

  private extractErrorMessageFromService(serviceError: any): string {
    if (!serviceError) return 'Unknown service error';
    if (serviceError.message) return serviceError.message;
    if (serviceError.error && serviceError.error.message) return serviceError.error.message;
    if (typeof serviceError === 'string') return serviceError;
    return 'Authentication service error';
  }

  private getUserFriendlyErrorMessage(errorCode: string, technicalMessage: string): string {
    const friendlyMessages: { [key: string]: string } = {
      'NO_AUTHORIZATION_CODE': 'No authorization code received. Please try logging in again.',
      'NO_USER_INFO': 'Unable to retrieve user information. Please contact support.',
      'CPR_NOT_FOUND': 'Unable to find your national ID information. Please ensure your eKey account has valid national ID details.',
      'NAVIGATION_ERROR': 'Unable to redirect to login page. Please try again.',
      'AUTHENTICATION_ERROR': 'Authentication failed. Please check your credentials and try again.',
      'HANDLE_CALLBACK_ERROR': 'Failed to process authentication response. Please try again.',
      'PROCESS_PARAMS_ERROR': 'Failed to process authentication parameters. Please try again.'
    };

    return friendlyMessages[errorCode] || technicalMessage;
  }

  private showLoader(): void {
    this.isLoading = true;
    LoaderService.showLoader();
  }

  private hideLoader(): void {
    this.isLoading = false;
    LoaderService.hideLoader();
  }

  private extractUserInfo(response: any): any {
    if (!response) return null;

    if (response && response.Data && response.Data.UserInfo) {
      return response.Data.UserInfo;
    }
    if (response && response.Data && response.Data.userInfo) {
      return response.Data.userInfo;
    }
    if (response && response.Data && response.Data.Cpr) {
      return response.Data;
    }
    if (response && response.UserInfo) {
      return response.UserInfo;
    }
    if (response && response.Cpr) {
      return response;
    }

    return null;
  }

  private extractCPR(userInfo: any): string | null {
    if (!userInfo) return null;

    const possibleCprFields = ['Cpr', 'cpr', 'CPR', 'nationalId', 'identityNumber', 'ssn'];
    
    for (const field of possibleCprFields) {
      if (userInfo[field]) {
        const cprValue = userInfo[field].toString().trim();
        if (this.isValidCPR(cprValue)) {
          return cprValue;
        }
      }
    }

    return null;
  }

  private isValidCPR(value: any): boolean {
    if (!value) return false;
    const strValue = value.toString().trim();
    const digitsOnly = strValue.replace(/\D/g, '');
    return digitsOnly.length === 9 && /^\d+$/.test(digitsOnly);
  }

  retry(): void {
    this.hideLoader();
    this.error = null;
    this.cprValue = null;
    this.userInfo = null;
    this.isLoading = false;
    this.isErrorHandled = false;
    this.router.navigate(['/login']);
  }
}